import { ReactNode, useState, useCallback } from "react";
import clsx from "clsx";
import { Checkbox } from "react-migration/components/Checkbox";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

interface PrintableLayerControlsProps {
  layerId: string;
  titleKey?: string;
  directTitle?: string;
  sectionIds?: string[];
  sectionLabels?: Record<string, string>;
  checked: boolean | "indeterminate";
  onCheckedChange: () => void;
  children?: ReactNode;
  hiddenSectionIds?: string[];
  toggleSection?: (sectionId: string) => void;
  indicator?: ReactNode;
  hideExpandButton?: boolean;
}

export function PrintableLayerControls({
  layerId,
  titleKey,
  directTitle,
  sectionIds = [],
  sectionLabels,
  checked,
  onCheckedChange,
  children,
  hiddenSectionIds = [],
  toggleSection,
  indicator,
  hideExpandButton = false,
}: Readonly<PrintableLayerControlsProps>) {
  const [expanded, setExpanded] = useState(true);
  const { t, checkKey } = useTranslation();

  let displayTitle: string;
  if (directTitle) {
    displayTitle = directTitle;
  } else if (titleKey) {
    displayTitle = t(titleKey);
  } else {
    displayTitle = t(`site_report.layers.${layerId.toLowerCase()}`);
  }

  const hasExpandableContent = !!children || (sectionIds.length > 0 && !!toggleSection);

  const handleMainCheckboxChange = useCallback(() => {
    onCheckedChange();
  }, [onCheckedChange]);

  const handleSectionToggle = useCallback(
    (sectionId: string) => {
      if (toggleSection) {
        toggleSection(sectionId);
      }
    },
    [toggleSection]
  );

  return (
    <div className="flex flex-col gap-2" data-testid={`${layerId}-controls`}>
      <div className="flex items-center gap-2 px-[2px]">
        <Checkbox
          id={`${layerId}-sections`}
          checked={checked}
          onCheckedChange={handleMainCheckboxChange}
          supportsIndeterminate
        />
        <label htmlFor={`${layerId}-sections`} className="text-sm select-none text-blue-grey-950">
          {displayTitle}
          {!!indicator && <span className="relative ml-2">{indicator}</span>}
        </label>
        {hasExpandableContent && !hideExpandButton && (
          <button className="ml-auto" data-testid={`${layerId}-expand-trigger`} onClick={() => setExpanded(!expanded)}>
            <i
              className={clsx(
                "text-base",
                "w-5",
                "h-5",
                "px-2",
                "flex",
                "items-center",
                "transition-transform",
                expanded ? "icon-lt-arrow-down-s-line" : "icon-lt-arrow-up-s-line"
              )}
            />
          </button>
        )}
      </div>

      {expanded && hasExpandableContent && (
        <div className="flex flex-col gap-2 ml-4" data-testid={`${layerId}-expand-content`}>
          {children}

          {toggleSection &&
            sectionIds.map((sec) => (
              <div className="flex items-center gap-2" key={sec}>
                <Checkbox
                  id={`${layerId}-${sec}`}
                  checked={!hiddenSectionIds.includes(sec)}
                  onCheckedChange={() => handleSectionToggle(sec)}
                />
                <label htmlFor={`${layerId}-${sec}`} className="text-sm select-none text-blue-grey-950">
                  {sectionLabels?.[sec] && checkKey(sectionLabels[sec])
                    ? t(sectionLabels[sec])
                    : t(`${titleKey}.${sec}`)}
                </label>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
